import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const AssignedTrucksModal = ({ booking, onClose, onEditAssignment }) => {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTrucks = useCallback(async () => {
    if (!booking?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('order_truck_assignments')
        ?.select('*')
        ?.eq('booking_id', booking?.id)
        ?.order('assigned_at', { ascending: true });

      if (!error && data) {
        setTrucks(data);
      }
    } catch (err) {
      console.error('AssignedTrucksModal load error:', err);
    }
    setLoading(false);
  }, [booking?.id]);

  useEffect(() => {
    loadTrucks();

    // Real-time subscription for live updates
    const channel = supabase
      ?.channel(`assigned_trucks_modal_${booking?.id}`)
      ?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'order_truck_assignments',
        filter: `booking_id=eq.${booking?.id}`,
      }, () => {
        loadTrucks();
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  }, [loadTrucks, booking?.id]);

  // Totals
  const totalTrucks = trucks?.length;
  const totalAssignedQty = trucks?.reduce((s, t) => s + Number(t?.assigned_quantity || 0), 0);
  const totalFreight = trucks?.reduce((s, t) => s + (Number(t?.rate_per_ton || 0) * Number(t?.assigned_quantity || 0)), 0);

  const getStatusBadge = (status) => {
    const map = {
      assigned: { bg: '#dbeafe', color: '#1e40af', label: 'Assigned' },
      in_transit: { bg: '#fef3c7', color: '#92400e', label: 'In Transit' },
      delivered: { bg: '#dcfce7', color: '#166534', label: 'Delivered' },
      cancelled: { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
    };
    const s = map?.[status] || map?.assigned;
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
        style={{ background: s?.bg, color: s?.color }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: s?.color }} />
        {s?.label}
      </span>
    );
  };

  const bookingTypeLabel = (bt) => {
    if (!bt) return 'Sugar Only';
    const lower = bt?.toLowerCase();
    if (lower?.includes('sugar') && lower?.includes('truck')) return 'Sugar + Truck';
    if (lower?.includes('bundle')) return 'Bundle';
    return bt;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 px-4 py-6 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl">

        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: '#bbf7d0', background: '#f0fdf4' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: '#dcfce7' }}
            >
              <Icon name="Truck" size={20} color="#16a34a" />
            </div>
            <div>
              <h3 className="text-base font-bold" style={{ color: '#1f2937' }}>
                Assigned Trucks for {booking?.offerId}
              </h3>
              <p className="text-xs" style={{ color: '#6b7280' }}>
                {booking?.factoryName} · {booking?.sugarGrade} · {Number(booking?.quantity)?.toLocaleString('en-IN')} MT
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-green-100 transition-colors"
          >
            <Icon name="X" size={18} color="#6b7280" />
          </button>
        </div>

        {/* Booking Summary */}
        <div className="px-6 py-4 border-b" style={{ borderColor: '#f3f4f6' }}>
          <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: '#9ca3af' }}>
            Booking Summary
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div className="rounded-xl px-3 py-2.5" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <p className="text-xs" style={{ color: '#9ca3af' }}>Buyer Email</p>
              <p className="text-xs font-semibold mt-0.5 break-all" style={{ color: '#1e293b' }}>
                {booking?.buyer || '—'}
              </p>
            </div>
            <div className="rounded-xl px-3 py-2.5" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <p className="text-xs" style={{ color: '#9ca3af' }}>Total Booked Qty</p>
              <p className="text-sm font-bold mt-0.5" style={{ color: '#166534' }}>
                {Number(booking?.quantity)?.toLocaleString('en-IN')} MT
              </p>
            </div>
            <div className="rounded-xl px-3 py-2.5" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <p className="text-xs" style={{ color: '#9ca3af' }}>Grade</p>
              <p className="text-sm font-bold mt-0.5" style={{ color: '#374151' }}>
                {booking?.sugarGrade || '—'}
              </p>
            </div>
            <div className="rounded-xl px-3 py-2.5" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <p className="text-xs" style={{ color: '#9ca3af' }}>Booking Type</p>
              <p className="text-xs font-semibold mt-0.5" style={{ color: '#0369a1' }}>
                {bookingTypeLabel(booking?.bookingType)}
              </p>
            </div>
            <div className="rounded-xl px-3 py-2.5" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <p className="text-xs" style={{ color: '#9ca3af' }}>Status</p>
              <p className="text-xs font-semibold mt-0.5 capitalize" style={{ color: '#166534' }}>
                {booking?.status || '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Trucks Table */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#9ca3af' }}>
              Assigned Trucks
            </p>
            {!loading && totalTrucks > 0 && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                style={{ background: '#dcfce7', color: '#166534' }}
              >
                <Icon name="Truck" size={12} color="#16a34a" />
                {totalTrucks} truck{totalTrucks !== 1 ? 's' : ''} allotted
              </span>
            )}
          </div>

          {loading && (
            <div className="flex items-center justify-center py-10">
              <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin mr-2" />
              <p className="text-sm" style={{ color: '#6b7280' }}>Loading truck assignments…</p>
            </div>
          )}

          {!loading && trucks?.length === 0 && (
            <div
              className="text-center py-10 rounded-xl"
              style={{ background: '#f9fafb', border: '1px dashed #d1d5db' }}
            >
              <Icon name="Truck" size={32} color="#d1d5db" />
              <p className="mt-2 text-sm font-medium" style={{ color: '#9ca3af' }}>No trucks assigned yet</p>
              <p className="text-xs mt-1" style={{ color: '#6b7280' }}>
                Click "Edit Assignment" to assign trucks to this booking
              </p>
            </div>
          )}

          {!loading && trucks?.length > 0 && (
            <div
              className="rounded-xl overflow-hidden"
              style={{ border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      {[
                        '#',
                        'Vehicle Number',
                        'Assigned Qty (MT)',
                        'Rate per Ton',
                        'Freight Amount',
                        'Destination',
                        'Driver Name',
                        'Driver Phone',
                        'Truck Marketplace Offer ID',
                        'Status',
                      ]?.map(col => (
                        <th
                          key={col}
                          className="px-3 py-2.5 text-left text-xs font-semibold whitespace-nowrap"
                          style={{ color: '#374151', borderBottom: '1px solid #e5e7eb' }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {trucks?.map((truck, idx) => {
                      const freight = Number(truck?.rate_per_ton || 0) * Number(truck?.assigned_quantity || 0);
                      return (
                        <tr
                          key={truck?.id}
                          style={{
                            background: idx % 2 === 0 ? '#ffffff' : '#fafafa',
                            borderBottom: '1px solid #f3f4f6',
                          }}
                        >
                          <td className="px-3 py-2.5 text-xs font-semibold" style={{ color: '#9ca3af' }}>
                            {idx + 1}
                          </td>
                          <td className="px-3 py-2.5 font-semibold whitespace-nowrap" style={{ color: '#1e293b' }}>
                            {truck?.vehicle_number || '—'}
                          </td>
                          <td className="px-3 py-2.5 font-medium text-center" style={{ color: '#1e293b' }}>
                            {Number(truck?.assigned_quantity || 0)?.toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: '#374151' }}>
                            ₹{Number(truck?.rate_per_ton || 0)?.toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2.5 font-semibold whitespace-nowrap" style={{ color: '#7c3aed' }}>
                            ₹{freight?.toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2.5 text-xs" style={{ color: '#374151', maxWidth: '140px' }}>
                            {truck?.destination || '—'}
                          </td>
                          <td className="px-3 py-2.5 text-xs" style={{ color: '#374151' }}>
                            {truck?.driver_name || '—'}
                          </td>
                          <td className="px-3 py-2.5 text-xs whitespace-nowrap" style={{ color: '#374151' }}>
                            {truck?.driver_phone || '—'}
                          </td>
                          <td className="px-3 py-2.5 text-xs" style={{ color: '#0369a1' }}>
                            {truck?.readable_booking_id || truck?.offer_id || truck?.truck_marketplace_offer_id || '—'}
                          </td>
                          <td className="px-3 py-2.5">
                            {getStatusBadge(truck?.status)}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Totals Row */}
                    <tr style={{ background: '#f0fdf4', borderTop: '2px solid #bbf7d0' }}>
                      <td
                        colSpan={2}
                        className="px-3 py-3 text-xs font-bold"
                        style={{ color: '#166534' }}
                      >
                        TOTALS ({totalTrucks} truck{totalTrucks !== 1 ? 's' : ''})
                      </td>
                      <td className="px-3 py-3 font-bold text-center" style={{ color: '#166534' }}>
                        {totalAssignedQty?.toLocaleString('en-IN')} MT
                      </td>
                      <td className="px-3 py-3 text-xs" style={{ color: '#9ca3af' }}>—</td>
                      <td className="px-3 py-3 font-bold whitespace-nowrap" style={{ color: '#7c3aed' }}>
                        ₹{totalFreight?.toLocaleString('en-IN')}
                      </td>
                      <td colSpan={5} className="px-3 py-3 text-xs" style={{ color: '#9ca3af' }}>—</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 border-t flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{ borderColor: '#f3f4f6', background: '#fafafa' }}
        >
          {/* Summary chips */}
          <div className="flex flex-wrap gap-3">
            <div className="rounded-lg px-3 py-2" style={{ background: '#fef3c7' }}>
              <p className="text-xs" style={{ color: '#9ca3af' }}>Total Trucks</p>
              <p className="font-bold text-sm" style={{ color: '#92400e' }}>{totalTrucks}</p>
            </div>
            <div className="rounded-lg px-3 py-2" style={{ background: '#dcfce7' }}>
              <p className="text-xs" style={{ color: '#9ca3af' }}>Total Assigned Qty</p>
              <p className="font-bold text-sm" style={{ color: '#166534' }}>{totalAssignedQty?.toLocaleString('en-IN')} MT</p>
            </div>
            <div className="rounded-lg px-3 py-2" style={{ background: '#ede9fe' }}>
              <p className="text-xs" style={{ color: '#9ca3af' }}>Total Freight</p>
              <p className="font-bold text-sm" style={{ color: '#7c3aed' }}>₹{totalFreight?.toLocaleString('en-IN')}</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50"
              style={{ border: '1px solid #d1d5db', color: '#374151' }}
            >
              Close
            </button>
            <button
              onClick={() => { onClose?.(); onEditAssignment?.(); }}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90 flex items-center gap-2"
              style={{ background: '#d97706', color: '#ffffff' }}
            >
              <Icon name="Edit2" size={14} color="#fff" />
              Edit Assignment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignedTrucksModal;
